"use client";

import { useState } from "react";
import ScrollReveal from "../common/ScrollReveal";
import ExpandCollapseCard from "../common/ExpandCollapseCard";
import QSuiteAnimation from "../common/QSuiteAnimation";
import { AccordionEntry, qSuitePacks } from "./accordionContent";

function PackBody({ pack }: { pack: AccordionEntry }) {
  // The negative margin pulls back most of the card's title indent so this
  // page's long body copy runs nearly full width. It tracks that indent per
  // breakpoint (31px at sm+, 15px on phones) to leave a 16px step.
  return (
    <div className="flex flex-col gap-4 sm:gap-6 pt-2 -ml-[15px] sm:-ml-[31px]">
      <div className="flex flex-col gap-6 sm:gap-7 text-text-highlight text-[14px] sm:text-[15px] lg:text-[16px] leading-[24px] sm:leading-[26px] lg:leading-[28px] tracking-[0.5px]">
        {pack.headline && <p>{pack.headline}</p>}
        <p>{pack.description}</p>
        <p>Key Capabilities:</p>
      </div>

      {pack.coreCapabilities && pack.coreCapabilities.length > 0 && (
        <ul className="flex flex-col">
          {pack.coreCapabilities.map((item, i) => (
            <li key={i} className="flex items-start gap-6">
              <span
                aria-hidden
                className="mt-[10px] block h-2 w-2 shrink-0 rounded-full bg-primary"
              />
              <p className="flex-1 min-w-0 text-text-highlight text-[14px] sm:text-[15px] lg:text-[16px] leading-[24px] sm:leading-[26px] lg:leading-[28px] tracking-[0.5px]">
                {item}
              </p>
            </li>
          ))}
        </ul>
      )}

      {pack.footer && (
        <p className="text-text-highlight text-[14px] sm:text-[15px] lg:text-[16px] leading-[24px] sm:leading-[26px] lg:leading-[28px] tracking-[0.5px]">
          {pack.footer}
        </p>
      )}
    </div>
  );
}

export default function QSuiteProduct() {
  // Each pack expands independently; all start collapsed (Figma 731-74594).
  const [openIndexes, setOpenIndexes] = useState<Set<number>>(new Set());

  // Bottom padding and the block rhythm tighten one step on phones, where
  // vertical space is the scarce dimension.
  return (
    <section className="relative w-full px-4 sm:px-6 lg:px-8 pt-2 pb-12 sm:pb-16 overflow-hidden">
      <div className="max-w-[1300px] mx-auto flex flex-col gap-8 sm:gap-12 md:gap-16 lg:gap-12">
        {/* Top: intro text (left) + platform illustration (right) */}
        <div className="grid grid-cols-1 lg:grid-cols-[639fr_638fr] gap-10 lg:gap-6 items-center">
          <ScrollReveal direction="up" delay={0}>
            <div className="flex flex-col gap-4 text-text-highlight">
              <h2 className="font-[family-name:var(--font-avenir)] font-bold capitalize text-white text-[32px] sm:text-[40px] lg:text-[55px] leading-normal tracking-[0.2px]">
                Every security control makes a Promise.
                <br />
                Q-Suite Verifies.
              </h2>
              {/* Two paragraphs, not one <p> split by <br />: a line break gives
                  no paragraph gap at all, so these ran flush together. Own
                  wrapper so they get the site-wide 24/28px body rhythm while the
                  heading keeps its tighter gap-4 to the copy. */}
              <div className="flex flex-col gap-6 sm:gap-7 text-[14px] sm:text-[15px] lg:text-[16px] leading-[24px] sm:leading-[26px] lg:leading-[28px] tracking-[0.5px]">
                <p>
                  Q-Suite is ECQ&rsquo;s offensive security content ecosystem,
                  built from nearly two decades of vulnerability research,
                  exploit development, red teaming, OT security assessments, and
                  real-world attack operations.
                </p>
                <p>
                  Unlike traditional exploit packs that rely on static content
                  and infrequent updates, QSuite combines proprietary offensive
                  security intelligence, AI-assisted research workflows,
                  automated validation pipelines, and researcher-led quality
                  assurance to continuously deliver high-fidelity attack content
                  for modern security validation programs.
                </p>
              </div>
            </div>
          </ScrollReveal>

          {/* On mobile the animated visual leads (order-first) instead of
              sitting below the intro copy; on lg it returns to the right-hand
              column (source order). */}
          <ScrollReveal
            direction="right"
            delay={150}
            className="order-first lg:order-none"
          >
            <div className="relative w-full max-w-[644px] mx-auto lg:ml-auto">
              <QSuiteAnimation className="home-float relative w-full aspect-[638/436]" />
            </div>
          </ScrollReveal>
        </div>

        {/* Two-column pack accordion cards (Figma 731-74594) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-16 gap-y-8 items-start">
          {qSuitePacks.map((pack, index) => (
            <ScrollReveal
              key={pack.title}
              direction="up"
              delay={150 + index * 80}
            >
              <ExpandCollapseCard
                title={pack.title}
                isOpen={openIndexes.has(index)}
                onToggle={() =>
                  setOpenIndexes((prev) => {
                    const next = new Set(prev);
                    if (next.has(index)) next.delete(index);
                    else next.add(index);
                    return next;
                  })
                }
              >
                <PackBody pack={pack} />
              </ExpandCollapseCard>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
}
